import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  signal,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { SiteContextService } from '../../../services/site-context/site-context.service';

export type ButtonSize = 'small' | 'default' | 'quadratic';

export type ButtonStyle =
  | 'primary'
  | 'secondary'
  | 'primary-alt'
  | 'secondary-alt'
  | 'error'
  | 'error-alt'
  | 'dark'
  | 'tertiary'
  | 'custom';

const BUTTON_DISABLED =
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-standard-disabled disabled:border-standard-disabled disabled:text-standard-text-disabled';
const BUTTON_DISABLED_ALT =
  'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-transparent disabled:border-standard-text-disabled disabled:text-standard-text-disabled';

const BUTTON_PRIMARY =
  'focus:outline-hidden bg-primary-main hover:bg-primary-hover hover:border-primary-hover border-primary-main text-white hover:bg-info-main hover:border-info-main w-52 ' +
  BUTTON_DISABLED;
const BUTTON_SECONDARY =
  'focus:outline-hidden bg-secondary-main hover:bg-secondary-hover hover:border-secondary-dark text-secondary-on border-secondary-dark w-52 ' +
  BUTTON_DISABLED;
const BUTTON_PRIMARY_ALT =
  'focus:outline-hidden border-primary-main hover:bg-standard-hover-main bg-transparent w-max ' +
  BUTTON_DISABLED_ALT;
const BUTTON_PRIMARY_ALT_DARK =
  'focus:outline-hidden hover:bg-standard-hover-dark bg-transparent w-max ' +
  BUTTON_DISABLED_ALT;
const BUTTON_SECONDARY_ALT =
  'focus:outline-hidden text-secondary-main hover:bg-secondary-main/20 border-secondary-main bg-transparent w-max ' +
  BUTTON_DISABLED_ALT;
const BUTTON_ERROR =
  'border-error-main hover:border-error-hover bg-error-main hover:bg-error-hover text-white focus:outline-hidden ' +
  BUTTON_DISABLED;
const BUTTON_ERROR_ALT =
  'border-error-main bg-transparent hover:border-error-hover hover:bg-error-main/20 text-error-main hover:text-error-hover focus:outline-hidden ' +
  BUTTON_DISABLED_ALT;
const BUTTON_ERROR_ALT_DARK =
  'hover:bg-standard-hover-dark border-error-main bg-transparent hover:border-error-main text-error-main focus:outline-hidden' +
  BUTTON_DISABLED_ALT;
const BUTTON_CUSTOM =
  'border-transparent bg-transparent text-standard-text-primary hover:bg-standard-hover-main focus:outline-none focus-visible:outline-1 focus-visible:outline-solid ' +
  BUTTON_DISABLED_ALT;
const BUTTON_TERTIARY =
  'focus:outline-hidden w-max border-transparent bg-transparent hover:bg-standard-hover-main text-standard-text-secondary enabled:hover:border-standard-text-secondary enabled:active:border-standard-text-secondary disabled:cursor-not-allowed disabled:border-transparent disabled:text-standard-text-disabled';

@Component({
  selector: 'aw-button',
  imports: [CommonModule, FormsModule, AppendTestIdPipe],
  templateUrl: './button.component.html',
  styles: `
    .tertiary-button:enabled:hover {
      background-color: color-mix(
        in srgb,
        var(--tertiary-background-color, var(--color-standard-text-secondary))
          6%,
        transparent
      );
    }

    .tertiary-button:enabled:active {
      background-color: color-mix(
        in srgb,
        var(--tertiary-background-color, var(--color-standard-text-secondary))
          12%,
        transparent
      );
    }
  `,
})
export class ButtonComponent {
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly siteContextService: SiteContextService =
    inject(SiteContextService);
  public id: InputSignal<string> = input.required();
  public text: InputSignal<string | undefined> = input<string>();
  public type: InputSignal<'submit' | 'button' | 'reset'> = input.required();
  public disabledText: InputSignal<string> = input<string>('Button disabled');
  public isDisabled: InputSignal<boolean | undefined> = input<boolean>();
  public isFullWidth: InputSignal<boolean> = input<boolean>(true);
  public style: InputSignal<ButtonStyle> = input<ButtonStyle>('primary');
  public buttonSize: InputSignal<ButtonSize> = input<ButtonSize>('default');
  public isModalButton: InputSignal<boolean> = input<boolean>(false);
  public flattenRightSide: InputSignal<boolean> = input<boolean>(false);
  public flattenLeftSide: InputSignal<boolean> = input<boolean>(false);

  public defaultButtonColour: Signal<string> = computed((): string => {
    return this.determineButtonColour(this.style());
  });

  protected readonly signal: typeof signal = signal;

  public corporateBackgroundColor: Signal<string> = computed(() => {
    if (this.siteContextService.isCmsContext()) {
      return '';
    }
    const cDesign = this.corporateDesignService.corporateDesign();

    if (this.isDisabled()) {
      return !(this.style().includes('alt') || this.style() === 'tertiary')
        ? 'var(--color-background-disabled)'
        : '';
    }
    switch (this.style()) {
      case 'primary':
        if (this.corporateDesignService.isDarkModeActive()) {
          return cDesign.primaryDarkColor;
        }
        return cDesign.primaryColor;
      case 'secondary':
        if (this.corporateDesignService.isDarkModeActive()) {
          return '#FFFFFF';
        }
        return cDesign.secondaryColor;
      case 'error':
      case 'error-alt':
        return '#CE4545';
      default:
        return '';
    }
  });

  public corporateBorderColor: Signal<string> = computed(() => {
    if (this.siteContextService.isCmsContext()) {
      return '';
    }
    const cDesign = this.corporateDesignService.corporateDesign();

    if (this.isDisabled()) {
      return !(this.style().includes('alt') || this.style() === 'tertiary')
        ? ''
        : 'var(--color-background-disabled)';
    }
    switch (this.style()) {
      case 'primary':
        return cDesign.primaryBorderColor;
      case 'primary-alt':
        return this.corporateDesignService.linkIconColor();
      case 'secondary':
        if (this.corporateDesignService.isDarkModeActive()) {
          return '#000';
        }
        return cDesign.secondaryFontColor;
      case 'secondary-alt':
        return cDesign.secondaryColor;
      case 'error':
      case 'error-alt':
        return '#CE4545';
      case 'tertiary':
        return 'transparent';
      default:
        return '';
    }
  });

  public corporateFontColor: Signal<string> = computed(() => {
    if (this.siteContextService.isCmsContext()) {
      return '';
    }
    const cDesign = this.corporateDesignService.corporateDesign();

    if (this.isDisabled()) {
      return 'var(--color-standard-text-disabled)';
    }
    switch (this.style()) {
      case 'primary':
        if (this.corporateDesignService.isDarkModeActive()) {
          return cDesign.primaryDarkFontColor;
        }
        return cDesign.primaryFontColor;
      case 'primary-alt':
        return this.corporateDesignService.linkIconColor();
      case 'secondary':
        if (this.corporateDesignService.isDarkModeActive()) {
          return '#000';
        }
        return cDesign.secondaryFontColor;
      case 'tertiary':
        return this.corporateDesignService.linkIconColor();
      case 'secondary-alt':
        return cDesign.secondaryColor;
      case 'error':
      case 'error-alt':
        return '#FFF';
      default:
        return '';
    }
  });

  private determineButtonColour(style: ButtonStyle): string {
    switch (style) {
      case 'secondary':
        return BUTTON_SECONDARY;
      case 'primary-alt':
        return this.corporateDesignService.isDarkModeActive()
          ? BUTTON_PRIMARY_ALT_DARK
          : BUTTON_PRIMARY_ALT;
      case 'secondary-alt':
        return BUTTON_SECONDARY_ALT;
      case 'error':
        return BUTTON_ERROR;
      case 'error-alt':
        return this.corporateDesignService.isDarkModeActive()
          ? BUTTON_ERROR_ALT_DARK
          : BUTTON_ERROR_ALT;
      case 'custom':
        return BUTTON_CUSTOM;
      case 'tertiary':
        return BUTTON_TERTIARY;
      default:
        return BUTTON_PRIMARY;
    }
  }
}
